import { Outlet, Link, createRootRouteWithContext, HeadContent, Scripts, useLocation, useRouter } from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect, useState, useRef } from "react";
import { getPixelId, trackPageView, setAdvancedMatching, ensurePixelIdentity } from "@/lib/metaPixel";
import { useAuth } from "@/hooks/useAuth";
import { captureAffiliateRef } from "@/lib/affiliate";
import { Toaster } from "sonner";
import { AnnouncementBar } from "@/components/AnnouncementBar";
import { Header } from "@/components/Header";
import { CartDrawer } from "@/components/CartDrawer";
import { Footer } from "@/components/Footer";
import { PromoCaptchaModal } from "@/components/PromoCaptchaModal";
import promoImage from "@/assets/promo-captcha-hats.webp";
import { useCartSync } from "@/hooks/useCartSync";
import { useCartStore } from "@/stores/cartStore";
import { getSiteSettings } from "@/lib/siteSettings.functions";
import { SiteSettingsProvider, SITE_SETTINGS_QUERY_KEY, useSiteSettings } from "@/hooks/useSiteSettings";

import appCss from "../styles.css?url";

interface RouterContext {
  queryClient: QueryClient;
}

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="font-heading text-7xl text-primary">404</h1>
        <h2 className="mt-4 font-ui text-xl font-semibold text-primary">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">The page you're looking for doesn't exist or has been moved.</p>
        <div className="mt-6">
          <Link to="/" className="btn-cc-accent tracking-wider">Go home</Link>
        </div>
      </div>
    </div>
  );
}

function RootErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  const router = useRouter();

  useEffect(() => {
    console.error(error);
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="font-heading text-4xl text-primary">Something went wrong</h1>
        <p className="mt-2 text-sm text-muted-foreground">Please refresh the page and try again.</p>
        <div className="mt-6 flex items-center justify-center gap-3">
          <button onClick={() => { router.invalidate(); reset(); }} className="btn-cc-primary tracking-wider">Try again</button>
          <a href="/" className="btn-cc-accent tracking-wider">Go home</a>
        </div>
      </div>
    </div>
  );
}

const pixelBaseCode = `(function(){
try {
  var _gpc = (typeof navigator !== 'undefined' && navigator.globalPrivacyControl === true);
  var _oo = false;
  try { _oo = window.localStorage.getItem('ccp_privacy_optout') === '1'; } catch(e){}
  if (_gpc || _oo) { return; }
} catch(e){}
!function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window, document,'script',
'https://connect.facebook.net/en_US/fbevents.js');
fbq('init', '${getPixelId()}');
fbq('track', 'PageView');
})();`;

export const Route = createRootRouteWithContext<RouterContext>()({
  loader: async ({ context }) => {
    const siteSettings = await getSiteSettings();
    context.queryClient.setQueryData(SITE_SETTINGS_QUERY_KEY, siteSettings);
    return { siteSettings };
  },
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { property: "og:site_name", content: "Coyote Creek Hat Co." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "google-site-verification", content: "gcxpVZLGtlb_qEEcoG7wNJEQskRjeRRLor2JOi1uFks" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      { rel: "stylesheet", href: "https://fonts.googleapis.com/css2?family=League+Spartan:wght@600;700;800;900&family=Montserrat:wght@400;500;600;700&display=swap" },
      { rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
    ],
    scripts: [
      { type: "text/javascript", children: pixelBaseCode },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  errorComponent: RootErrorComponent,
  notFoundComponent: NotFoundComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <noscript>
          <img
            height="1"
            width="1"
            style={{ display: "none" }}
            src={`https://www.facebook.com/tr?id=${getPixelId()}&ev=PageView&noscript=1`}
            alt=""
          />
        </noscript>
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const { siteSettings } = Route.useLoaderData();

  return (
    <QueryClientProvider client={queryClient}>
      <SiteSettingsProvider settings={siteSettings}>
        <AppLayout />
      </SiteSettingsProvider>
    </QueryClientProvider>
  );
}

const SESSION_KEY = "ccp_seen";
const DELAY_MS = 14000;

function AppLayout() {
  const location = useLocation();
  const isOpen = useCartStore(s => s.isOpen);
  const openCart = useCartStore(s => s.openCart);
  const closeCart = useCartStore(s => s.closeCart);
  useCartSync();
  const { user } = useAuth();
  const { data: siteSettings } = useSiteSettings();
  const announcementMessages = (siteSettings?.announcements?.length
    ? siteSettings.announcements
    : [siteSettings?.announcement_text ?? ""]
  ).filter((m) => m && m.trim().length > 0);
  const hasAnnouncement = !!siteSettings?.announcement_enabled && announcementMessages.length > 0;

  useEffect(() => {
    ensurePixelIdentity();
  }, []);

  useEffect(() => {
    if (user?.email) {
      setAdvancedMatching({ em: user.email });
      return;
    }
    try {
      const stored = sessionStorage.getItem("ccp_am_email");
      if (stored) {
        setAdvancedMatching({ em: stored });
      } else {
        ensurePixelIdentity();
      }
    } catch {
      ensurePixelIdentity();
    }
  }, [user?.email]);

  const [promoOpen, setPromoOpen] = useState(false);
  const [tagVisible, setTagVisible] = useState(false);
  const promoWasOpened = useRef(false);

  // Track PageView on route changes (skip initial load — base script handles it)
  const hasTrackedInitial = useRef(false);
  useEffect(() => {
    if (!hasTrackedInitial.current) {
      hasTrackedInitial.current = true;
      captureAffiliateRef();
      return;
    }
    captureAffiliateRef();
    trackPageView();
  }, [location.pathname]);

  // Strip Google Merchant Center auto-tagging params (srsltid, srslt, etc.)
  // from the address bar after load so visitors see clean URLs.
  // Tracking is already captured before this runs, so analytics stay intact.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    let changed = false;
    for (const key of [...url.searchParams.keys()]) {
      if (key.startsWith("srsl")) {
        url.searchParams.delete(key);
        changed = true;
      }
    }
    if (changed) {
      const clean = url.pathname + (url.searchParams.toString() ? "?" + url.searchParams.toString() : "") + url.hash;
      window.history.replaceState(window.history.state, "", clean);
    }
  }, [location.pathname]);

  // Auto-open timer. Tag only appears after the popup has been shown and dismissed.
  useEffect(() => {
    if (typeof window === "undefined") return;

    let alreadySeen = false;
    try {
      alreadySeen = !!sessionStorage.getItem(SESSION_KEY);
    } catch {
      /* sessionStorage unavailable */
    }

    if (alreadySeen) {
      setTagVisible(true);
      return;
    }

    // Warm the promo image cache so the modal renders instantly
    const img = new Image();
    img.src = promoImage;
    const t = window.setTimeout(() => {
      setPromoOpen(true);
      promoWasOpened.current = true;
    }, DELAY_MS);
    return () => window.clearTimeout(t);
  }, []);

  // Show tag once the popup is dismissed
  useEffect(() => {
    if (!promoOpen && promoWasOpened.current) {
      setTagVisible(true);
      try {
        sessionStorage.setItem(SESSION_KEY, "1");
      } catch {
        /* sessionStorage unavailable */
      }
    }
  }, [promoOpen]);

  function dismissTag() {
    setTagVisible(false);
  }

  function openPromo() {
    setPromoOpen(true);
    promoWasOpened.current = true;
  }

  const showTag = tagVisible && !promoOpen;

  return (
    <div className="min-h-screen bg-background">
      <div className="fixed inset-x-0 top-0 z-50">
        <AnnouncementBar />
        <Header onCartOpen={openCart} />
      </div>
      <main className={hasAnnouncement ? "pt-[124px]" : "pt-[88px]"}>
        <Outlet />
      </main>
      <Footer />
      <CartDrawer isOpen={isOpen} onClose={closeCart} />
      <PromoCaptchaModal open={promoOpen} onOpenChange={setPromoOpen} />
      <Toaster position="bottom-right" />

      {showTag && (
        <div className="fixed bottom-4 left-4 z-[90]">
          <div
            className="promo-tag relative flex items-stretch overflow-hidden rounded-[6px] border-[3px] border-[#2A1510] bg-white shadow-[3px_3px_0_0_#2A1510] active:translate-x-[2px] active:translate-y-[2px] active:shadow-[1px_1px_0_0_#2A1510]"
          >
            <div className="promo-tag-fill pointer-events-none absolute inset-0 z-0 bg-blaze" />
            <button
              onClick={openPromo}
              className="promo-tag-text relative z-10 px-4 py-3 font-ui text-xs font-bold uppercase text-[#F5EFE2] md:px-5 md:py-4"
            >
              Buy 2 Get 1 Free
            </button>
            <button
              onClick={dismissTag}
              aria-label="Close offer"
              className="promo-tag-text relative z-10 grid w-9 place-items-center text-[#F5EFE2] md:w-10"
            >
              <span className="block text-base font-bold leading-none md:text-lg">×</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
